import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, ShoppingBag, Check, ChevronLeft, ChevronRight, X, Play, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, navigate]);

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!notifyEmail || !product) return;
    
    setNotifyLoading(true);
    try {
      await axios.post(`${API}/notify-restock`, {
        email: notifyEmail,
        product_id: product.id,
        product_name: product.name,
        product_slug: product.slug
      });
      setNotifySubmitted(true);
    } catch (error) {
      console.error('Error submitting notification request:', error);
    } finally {
      setNotifyLoading(false);
    }
  };

  const nextImage = () => {
    if (product && product.images) {
      setSelectedImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product && product.images) {
      setSelectedImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, product]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-neutral-500">Product not found</p>
      </div>
    );
  }

  const allImages = product.images || [];
  const hasVideo = product.video_url;

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-24 md:pt-0 bg-white">
      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-6 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-2"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={48} />
            </button>
            
            <motion.img
              key={selectedImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={allImages[selectedImageIndex]}
              alt={`${product.name} - Image ${selectedImageIndex + 1}`}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-6 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-2"
              data-testid="lightbox-next"
            >
              <ChevronRight size={48} />
            </button>
            
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm">
              {selectedImageIndex + 1} / {allImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lg:grid lg:grid-cols-2 min-h-screen">
        {/* Image Gallery Section */}
        <div className="lg:sticky lg:top-0 lg:h-screen bg-neutral-100 flex flex-col">
          {/* Main Image or Video */}
          <div className="flex-1 relative flex items-center justify-center p-4 lg:p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative w-full h-full flex items-center justify-center"
            >
              {showVideo && hasVideo ? (
                <video
                  src={product.video_url}
                  data-testid="product-video"
                  className="max-w-full max-h-[50vh] lg:max-h-[65vh] object-contain"
                  controls
                  autoPlay
                  loop
                />
              ) : allImages.length > 0 ? (
                <img
                  src={allImages[selectedImageIndex]}
                  alt={`${product.name} - Image ${selectedImageIndex + 1}`}
                  data-testid="product-main-image"
                  className="max-w-full max-h-[50vh] lg:max-h-[65vh] object-contain cursor-zoom-in transition-transform hover:scale-105"
                  onClick={() => openLightbox(selectedImageIndex)}
                />
              ) : (
                <div className="w-full aspect-square bg-neutral-200 flex items-center justify-center text-neutral-400">
                  No Image
                </div>
              )}
              
              {/* Navigation Arrows - only show for images */}
              {!showVideo && allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </motion.div>
          </div>

          {/* Thumbnail Gallery */}
          {(allImages.length > 1 || hasVideo) && (
            <div className="px-4 pb-4 lg:px-8 lg:pb-8">
              <div className="flex gap-2 overflow-x-auto py-2 scrollbar-hide justify-center">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => { setSelectedImageIndex(index); setShowVideo(false); }}
                    data-testid={`thumbnail-${index}`}
                    className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === selectedImageIndex && !showVideo
                        ? 'border-[#e84628] ring-2 ring-[#e84628]/20'
                        : 'border-transparent hover:border-neutral-300'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
                
                {/* Video Thumbnail */}
                {hasVideo && (
                  <button
                    onClick={() => setShowVideo(true)}
                    data-testid="video-thumbnail"
                    className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-all relative ${
                      showVideo
                        ? 'border-[#e84628] ring-2 ring-[#e84628]/20'
                        : 'border-transparent hover:border-neutral-300'
                    }`}
                  >
                    <video
                      src={product.video_url}
                      className="w-full h-full object-cover"
                      muted
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Play size={20} className="text-white" fill="white" />
                    </div>
                  </button>
                )}
              </div>
              
              {/* Image/Video Counter */}
              <p className="text-center text-sm text-neutral-400 mt-2">
                {showVideo ? 'Video' : `${selectedImageIndex + 1} of ${allImages.length} images`}
              </p>
            </div>
          )}
        </div>

        {/* Details Section */}
        <div className="p-6 lg:p-12 lg:pt-24 flex flex-col">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-black uppercase tracking-wider text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            Back
          </motion.button>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <span className="text-[#e84628] uppercase tracking-[0.2em] text-sm font-medium">
              {product.movie_reference || 'Sculpture'}
            </span>
            <h1 
              data-testid="product-name"
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-2 text-black"
            >
              {product.name}
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <span 
              data-testid="product-price"
              className="text-3xl lg:text-4xl font-bold text-black"
            >
              £{product.price.toFixed(2)}
            </span>
            <span className="text-neutral-400 ml-2">{product.currency}</span>
            <p className="text-sm text-neutral-500 mt-1">Taxes included.</p>
          </motion.div>

          {/* Description - Rendered as HTML */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="product-description"
            className="mt-8 product-description prose prose-neutral max-w-none"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />

          {/* Quantity */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <label className="text-neutral-400 uppercase tracking-wider text-sm mb-4 block">
              Quantity
            </label>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                data-testid="decrease-quantity"
                className="p-3 border border-neutral-200 hover:border-black transition-colors"
              >
                <Minus size={18} />
              </button>
              <span 
                data-testid="quantity-display"
                className="text-xl w-12 text-center"
              >
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                data-testid="increase-quantity"
                className="p-3 border border-neutral-200 hover:border-black transition-colors"
              >
                <Plus size={18} />
              </button>
            </div>
          </motion.div>

          {/* Add to Cart */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <button
              onClick={handleAddToCart}
              data-testid="add-to-cart-button"
              disabled={added || product.stock === 0}
              className={`w-full py-5 font-semibold uppercase tracking-wider flex items-center justify-center gap-3 transition-all ${
                product.stock === 0
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : added
                  ? 'bg-green-600 text-white'
                  : 'bg-[#e84628] text-white hover:bg-[#d63d20]'
              }`}
            >
              {product.stock === 0 ? (
                'Sold Out'
              ) : added ? (
                <>
                  <Check size={20} />
                  Added to Cart
                </>
              ) : (
                <>
                  <ShoppingBag size={20} />
                  Add to Cart — £{(product.price * quantity).toFixed(2)}
                </>
              )}
            </button>
          </motion.div>

          {/* Stock Status */}
          {product.stock === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-6"
            >
              <p className="text-red-600 text-sm font-medium mb-4">
                Currently Sold Out
              </p>
              
              {/* Notify Me Form */}
              {!notifySubmitted ? (
                <form onSubmit={handleNotifySubmit} className="space-y-3">
                  <p className="text-neutral-600 text-sm">
                    Get notified when this item is back in stock
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="flex-1 px-4 py-3 border border-neutral-300 text-sm focus:outline-none focus:border-[#e84628]"
                    />
                    <button
                      type="submit"
                      disabled={notifyLoading}
                      className="px-6 py-3 bg-neutral-900 text-white text-sm font-semibold uppercase tracking-wider hover:bg-neutral-800 transition-colors flex items-center gap-2"
                    >
                      <Bell size={16} />
                      {notifyLoading ? 'Sending...' : 'Notify Me'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="bg-green-50 border border-green-200 px-4 py-3 flex items-center gap-2">
                  <Check size={18} className="text-green-600" />
                  <p className="text-green-700 text-sm">
                    We'll email you when this item is back in stock!
                  </p>
                </div>
              )}
            </motion.div>
          ) : product.stock > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-6 text-green-600 text-sm font-medium"
            >
              In Stock ({product.stock} available)
            </motion.p>
          )}
        </div>
      </div>

      {/* Product Description Styles */}
      <style jsx global>{`
        .product-description p {
          margin-bottom: 1rem;
          color: #525252;
          line-height: 1.7;
        }
        .product-description strong {
          color: #171717;
          font-weight: 600;
        }
        .product-description em {
          color: #e84628;
          font-style: normal;
          font-weight: 500;
        }
        .product-description ul {
          list-style: none;
          padding-left: 0;
          margin: 1.5rem 0;
        }
        .product-description li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
          color: #525252;
        }
        .product-description li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.5rem;
          width: 6px;
          height: 6px;
          background-color: #e84628;
          border-radius: 50%;
        }
        .product-description li strong {
          color: #171717;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
